/**
 * "What's new" helpers (docs/memory/whats-new.md). Tiny on purpose: this file
 * ships in whichever chunk mounts a launcher. The entry text itself lives in
 * ./entries.js and is only fetched (its own small chunk) when a module opens.
 */

export const WHATS_NEW_MODULES = Object.freeze(['ship', 'products', 'picking']);

export const WHATS_NEW_MODULE_LABELS = Object.freeze({
  ship: 'Ship',
  products: 'Products',
  picking: 'Picking'
});

export const ENTRY_ID_RE = /^[a-z0-9][a-z0-9-]{2,99}$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Lazily loads the entry list (separate chunk, cached by the module system). */
export function loadWhatsNewEntries() {
  return import(/* webpackChunkName: "whats-new-entries" */ './entries').then((mod) => mod.WHATS_NEW_ENTRIES || []);
}

/** Today's calendar day on this device, YYYY-MM-DD (benches and tablets run on Sydney time). */
export function localDay(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Entries for one module, newest first; registry order breaks ties within a day.
 * An entry with `replacedBy` (the id of a newer entry that describes the same
 * feature as it works now) is dropped once that entry exists, so staff who
 * missed both only ever read the current version.
 */
export function entriesForModule(entries, module) {
  const ids = new Set((entries || []).filter(Boolean).map((entry) => entry.id));
  return (entries || [])
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => entry && entry.module === module && !(entry.replacedBy && ids.has(entry.replacedBy)))
    .sort((a, b) => (a.entry.date === b.entry.date ? a.index - b.index : (a.entry.date < b.entry.date ? 1 : -1)))
    .map(({ entry }) => entry);
}

export function unseenEntries(entries, module, seen) {
  const seenMap = seen || {};
  return entriesForModule(entries, module).filter((entry) => !seenMap[entry.id]);
}

/** "Wed 8 Oct" for an entry date (treated as a calendar day, no time zone shift). */
export function formatEntryDate(day) {
  if (!DAY_RE.test(String(day || ''))) return '';
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Schema check used by the registry test (and safe to call at runtime).
 * Returns a list of problems; empty means the entry is valid.
 */
export function validateEntry(entry) {
  const problems = [];
  if (!entry || typeof entry !== 'object') return ['entry is not an object'];
  const label = entry.id || '(no id)';
  if (!ENTRY_ID_RE.test(String(entry.id || ''))) problems.push(`${label}: id must be a lowercase slug`);
  if (!WHATS_NEW_MODULES.includes(entry.module)) problems.push(`${label}: module must be one of ${WHATS_NEW_MODULES.join(', ')}`);
  if (!DAY_RE.test(String(entry.date || '')) || Number.isNaN(new Date(`${entry.date}T00:00:00Z`).getTime())) {
    problems.push(`${label}: date must be YYYY-MM-DD`);
  } else if (!String(entry.id || '').endsWith(entry.date)) {
    problems.push(`${label}: id must end with its date (${entry.date})`);
  }
  if (entry.replacedBy !== undefined && (!ENTRY_ID_RE.test(String(entry.replacedBy)) || entry.replacedBy === entry.id)) {
    problems.push(`${label}: replacedBy must be another entry's id`);
  }
  if (typeof entry.title !== 'string' || !entry.title.trim()) problems.push(`${label}: title required`);
  if (typeof entry.summary !== 'string' || !entry.summary.trim()) problems.push(`${label}: summary required`);
  if (!Array.isArray(entry.body) || entry.body.length === 0 || entry.body.some((line) => typeof line !== 'string' || !line.trim())) {
    problems.push(`${label}: body must be a non-empty list of strings`);
  }
  if (entry.images !== undefined) {
    if (!Array.isArray(entry.images)) problems.push(`${label}: images must be a list`);
    else entry.images.forEach((image, i) => {
      if (!image || typeof image.src !== 'string' || !/^\/whats-new\/(ship|products|picking)\/[a-z0-9-]+\.(webp|png|jpg)$/.test(image.src)) {
        problems.push(`${label}: images[${i}].src must be /whats-new/<module>/<name>.webp|png|jpg`);
      } else if (!image.src.startsWith(`/whats-new/${entry.module}/`)) {
        problems.push(`${label}: images[${i}] belongs under /whats-new/${entry.module}/`);
      }
      if (!image || typeof image.alt !== 'string' || !image.alt.trim()) problems.push(`${label}: images[${i}].alt required`);
    });
  }
  return problems;
}
